import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MediaSection from './components/MediaSection';
import ResourceCard from '@/components/feature/ResourceCard';
import {
  formatCount,
  formatDate,
  getCategoryById,
  getCategoryName,
  getRelatedResources,
  getResourceById,
  getSubCategoryLabel,
} from '@/lib/catalog';
import { useFavorites } from '@/hooks/useFavorites';
import { useSeo } from '@/hooks/useSeo';

export default function DetailPage() {
  const { resourceId } = useParams();
  const resource = getResourceById(resourceId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState('');

  const related = useMemo(
    () => (resource ? getRelatedResources(resource, 3) : []),
    [resource],
  );

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const category = resource ? getCategoryById(resource.category) : undefined;
  const favorite = resource ? isFavorite(resource.id) : false;

  useSeo({
    title: resource ? `${resource.title} - 免费91` : '资源不存在 - 免费91',
    description: resource
      ? resource.summary.slice(0, 80)
      : '该资源可能已被移除，请返回首页浏览其他免费内容。',
    keywords: resource
      ? `${resource.title},${resource.tags.join(',')},免费${getCategoryName(resource.category)}`
      : '免费91,免费资源',
  });

  if (!resource || !category) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <span className="inline-flex w-16 h-16 rounded-full bg-background-100 text-foreground-400 items-center justify-center text-3xl">
          <i className="ri-file-unknow-line"></i>
        </span>
        <h1 className="mt-5 font-heading font-semibold text-2xl text-foreground-950">资源不存在</h1>
        <p className="mt-2 text-foreground-600">你访问的资源可能已被移除或链接有误。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast('链接已复制，快去分享吧');
    } catch {
      setToast('复制失败，请手动复制地址栏链接');
    }
    setShareOpen(false);
  };

  const shareTo = (name: string) => {
    setToast(`已生成${name}分享入口（演示）`);
    setShareOpen(false);
  };

  const FACTS = [
    { label: '分类', value: getCategoryName(resource.category) },
    { label: '子分类', value: getSubCategoryLabel(resource.subCategory) },
    { label: '发布时间', value: formatDate(resource.publishedAt) },
    { label: '作者 / 来源', value: `${resource.author} · ${resource.source}` },
  ];

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 py-6">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${resource.category}`} className="hover:text-primary-700 transition-colors">
            {category.name}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900 line-clamp-1">{resource.title}</span>
        </nav>
      </div>

      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-primary-100 text-primary-800 text-xs font-medium mb-3">
              <i className={category.icon}></i>
              {category.name} · {getSubCategoryLabel(resource.subCategory)}
            </span>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 leading-snug">
              {resource.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line"></i>
                {resource.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {formatDate(resource.publishedAt)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {formatCount(resource.views)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-heart-line"></i>
                {formatCount(resource.favorites)}
              </span>
            </div>

            <div className="mt-6">
              <MediaSection resource={resource} />
            </div>

            <div className="mt-8">
              <h4 id="intro" className="font-heading font-semibold text-lg text-foreground-950">
                <a href="#intro" className="hover:text-primary-700 transition-colors">资源简介</a>
              </h4>
              <p className="mt-3 text-foreground-600 leading-relaxed">{resource.summary}</p>
            </div>

            <div className="mt-6">
              <h4 id="detail" className="font-heading font-semibold text-lg text-foreground-950">
                <a href="#detail" className="hover:text-primary-700 transition-colors">详细说明</a>
              </h4>
              <p className="mt-3 text-foreground-600 leading-relaxed">{resource.description}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {resource.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    toggleFavorite(resource.id);
                    setToast(favorite ? '已取消收藏' : '已加入收藏');
                  }}
                  className={`flex-1 h-11 rounded-md text-sm font-medium flex items-center justify-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                    favorite
                      ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                      : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  }`}
                >
                  <i className={favorite ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  {favorite ? '已收藏' : '收藏'}
                </button>
                <div className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setShareOpen((prev) => !prev)}
                    className="w-11 h-11 rounded-md border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700 flex items-center justify-center transition-colors cursor-pointer"
                    aria-label="分享"
                  >
                    <i className="ri-share-line text-lg"></i>
                  </button>
                  {shareOpen && (
                    <div className="absolute right-0 top-12 z-20 w-44 rounded-lg border border-background-200 bg-background-50 p-2 f91-fade-in">
                      <button type="button" onClick={copyLink} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer">
                        <i className="ri-link"></i>复制链接
                      </button>
                      <button type="button" onClick={() => shareTo('微信')} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer">
                        <i className="ri-wechat-line"></i>分享到微信
                      </button>
                      <button type="button" onClick={() => shareTo('微博')} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer">
                        <i className="ri-weibo-line"></i>分享到微博
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <dl className="mt-5 space-y-3">
                {FACTS.map((fact) => (
                  <div key={fact.label} className="flex items-start justify-between gap-4 text-sm">
                    <dt className="text-foreground-500 shrink-0">{fact.label}</dt>
                    <dd className="text-foreground-900 text-right">{fact.value}</dd>
                  </div>
                ))}
              </dl>

              <Link
                to={`/category/${resource.category}`}
                className="mt-5 flex items-center justify-between w-full px-4 py-3 rounded-md bg-background-100 hover:bg-secondary-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-2 text-sm text-foreground-800">
                  <i className={`${category.icon} text-secondary-700`}></i>
                  查看「{category.name}」更多资源
                </span>
                <i className="ri-arrow-right-line text-foreground-500"></i>
              </Link>

              <p className="mt-4 text-xs text-foreground-400 leading-relaxed">
                本站资源仅供学习交流，内容演示为主，请勿用于商业用途。
              </p>
            </div>
          </aside>
        </div>
      </div>

      <div className="w-full bg-background-100 border-y border-background-200 mt-14">
        <div className="w-full px-4 md:px-8 py-12">
          <div className="flex items-end justify-between gap-4 mb-6">
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">
              相关推荐
            </h2>
            <Link
              to={`/category/${resource.category}`}
              className="text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              更多同类资源
              <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {related.map((item) => (
              <ResourceCard key={item.id} resource={item} />
            ))}
          </div>
        </div>
      </div>

      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-8 z-50 px-5 py-3 rounded-lg bg-foreground-950 text-background-50 text-sm f91-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}